import type { ReactNode } from 'react'

export type CommentFormatType = 'bold' | 'italic' | 'underline'

export interface CommentFormatDefinition {
  type: CommentFormatType
  label: string
  title: string
  startToken: string
  endToken: string
  className: string
}

export const COMMENT_FORMATS: CommentFormatDefinition[] = [
  {
    type: 'bold',
    label: 'Ж',
    title: 'Жирный',
    startToken: '**',
    endToken: '**',
    className: 'font-extrabold',
  },
  {
    type: 'italic',
    label: 'К',
    title: 'Курсив',
    startToken: '_',
    endToken: '_',
    className: 'italic',
  },
  {
    type: 'underline',
    label: 'Ч',
    title: 'Подчёркнутый',
    startToken: '++',
    endToken: '++',
    className: 'underline underline-offset-2',
  },
]

interface FormatMatch {
  definition: CommentFormatDefinition
  start: number
  end: number
  innerStart: number
  innerEnd: number
}

export function applyCommentFormatToValue(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  definition: CommentFormatDefinition
): {
  value: string
  selectionStart: number
  selectionEnd: number
} {
  const selectedText = value.slice(selectionStart, selectionEnd)
  const beforeSelection = value.slice(0, selectionStart)
  const afterSelection = value.slice(selectionEnd)
  const wrappedValue = `${beforeSelection}${definition.startToken}${selectedText}${definition.endToken}${afterSelection}`

  return {
    value: wrappedValue,
    selectionStart: selectionStart + definition.startToken.length,
    selectionEnd: selectionEnd + definition.startToken.length,
  }
}

function findNextFormatMatch(text: string): FormatMatch | null {
  let bestMatch: FormatMatch | null = null

  for (const definition of COMMENT_FORMATS) {
    const start = text.indexOf(definition.startToken)
    if (start < 0) continue

    const searchFrom = start + definition.startToken.length
    const end = text.indexOf(definition.endToken, searchFrom)
    if (end < 0) continue

    const match: FormatMatch = {
      definition,
      start,
      end: end + definition.endToken.length,
      innerStart: searchFrom,
      innerEnd: end,
    }

    if (!bestMatch || match.start < bestMatch.start) {
      bestMatch = match
    }
  }

  return bestMatch
}

function renderPlainText(text: string, keyPrefix: string): ReactNode[] {
  if (!text) return []

  return text.split('\n').flatMap((part, index, parts) => {
    const nodes: ReactNode[] = []
    if (part) {
      nodes.push(part)
    }
    if (index < parts.length - 1) {
      nodes.push(<br key={`${keyPrefix}-br-${index}`} />)
    }
    return nodes
  })
}

function renderFormattedSegment(text: string, keyPrefix: string): ReactNode[] {
  const match = findNextFormatMatch(text)
  if (!match) {
    return renderPlainText(text, keyPrefix)
  }

  const before = text.slice(0, match.start)
  const inner = text.slice(match.innerStart, match.innerEnd)
  const after = text.slice(match.end)

  return [
    ...renderFormattedSegment(before, `${keyPrefix}-before`),
    renderCommentFormat(match.definition, renderFormattedSegment(inner, `${keyPrefix}-inner`), `${keyPrefix}-formatted`),
    ...renderFormattedSegment(after, `${keyPrefix}-after`),
  ]
}

function renderCommentFormat(
  definition: CommentFormatDefinition,
  children: ReactNode,
  key: string
): ReactNode {
  if (definition.type === 'bold') {
    return <strong key={key}>{children}</strong>
  }

  if (definition.type === 'italic') {
    return <em key={key}>{children}</em>
  }

  return <u key={key}>{children}</u>
}

export function renderFormattedComment(text: string): ReactNode {
  return <>{renderFormattedSegment(text, 'comment')}</>
}
